import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Close } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Box,
  Button,
  Dialog,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';

import {
  gray40,
  gray80,
  gray100,
  orange50,
  primaryButtonBackgroundColor,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
} from '../../../constants/colors';
import {
  boldMd,
  dialogTitle as dialogTitleStyle,
  regularLg,
  regularSm,
} from '../../../constants/location';
import { filterMatchMode, masterMatchThreshold, searchMatchMode } from '../../../constants/enum';
import {
  ColumnMapping,
  MasterFilterKey,
  MasterMappingType,
  MasterSearchKey,
  MatchMode,
} from '../../../types/master';
import { PatternType, ItemListType } from '../../../types/pattern';
import { getDictionariesByPatternId } from '../../../api/firebase/firestore/dictionary/readByPattern';
import { getMastersByPatternId } from '../../../api/firebase/firestore/master/readByPattern';
import { resolveFilterKeys, resolveSearchKey } from '../../../utils/master/matchKeys';
import CustomLoading from '../form/CustomLoading';
import CustomSelectField from '../form/CustomSelectField';
import CustomTextfield from '../form/CustomTextfield';

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '560px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  dialogTitle: {
    ...dialogTitleStyle,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  closeIcon: {
    height: '32px',
    width: '32px',
  },
  iconButton: {
    padding: 0,
  },
  fileSummary: {
    background: gray40,
    borderRadius: '4px',
    boxSizing: 'border-box',
    padding: '8px 12px',
    minHeight: '40px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
  },
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '24px',
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  submitButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  submitButtonText: {
    ...boldMd,
    color: white,
  },
  sectionLabel: {
    ...boldMd,
    marginBottom: '4px',
    color: gray80,
  },
  // STEP見出し（#3180）。設定の順序が一目で分かるようにする。
  stepHeader: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
    marginBottom: '4px',
  },
  stepBadge: {
    alignItems: 'center',
    backgroundColor: gray100,
    borderRadius: '4px',
    color: white,
    display: 'flex',
    flexShrink: 0,
    fontSize: '11px',
    fontWeight: 700,
    height: '20px',
    justifyContent: 'center',
    padding: '0 8px',
  },
  stepTitle: {
    ...boldMd,
    color: gray100,
  },
  stepOptional: {
    ...regularSm,
    color: gray80,
    flexShrink: 0,
  },
  sectionDescription: {
    fontSize: '11px',
    color: gray80,
    lineHeight: 1.4,
    marginBottom: '8px',
  },
  noteText: {
    fontSize: '11px',
    color: red50,
    lineHeight: 1.4,
  },
  // スコア入力の単位表示（#3263）。
  // 「% 以上で反映」は途中で改行させたくないため nowrap で固定する。
  unitText: {
    ...regularSm,
    color: gray80,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  keyRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '8px',
  },
  sectionDivider: {
    borderTop: '1px solid ' + gray40,
    paddingTop: '16px',
    marginBottom: '16px',
  },
  addButton: {
    ...secondaryButtonBackgroundColor,
    height: '36px',
    marginTop: '4px',
    '& .MuiButton-icon': { marginRight: '4px' },
  },
};

interface PatternItem {
  id: string;
  label: string;
  /**
   * 明細（配列）項目の子かどうか。スコープ選択時の補足表示に使う（#3072）。
   * 絞り込みの選択肢から外す判定にも使う（#3517）。
   */
  isArray?: boolean;
}

interface StepHeaderProps {
  step: number;
  title: string;
  /** 任意設定であることを示す（省略時は必須扱いで何も出さない） */
  optional?: boolean;
}

/** STEP見出し（#3180）。設定の順序と役割を明示する。 */
const StepHeader = ({ step, title, optional }: StepHeaderProps): ReactElement => (
  <Box sx={styles.stepHeader}>
    <Box component="span" sx={styles.stepBadge}>
      STEP{step}
    </Box>
    <Typography sx={styles.stepTitle}>{title}</Typography>
    {optional && <Typography sx={styles.stepOptional}>｜任意</Typography>}
  </Box>
);

/**
 * スコア入力を数字のみに正規化する（#3263）。
 * スコアは 0〜100 の整数（%）なので、小数点・符号・全角は受け付けない。
 * 桁数は最大3桁に制限し、範囲外の値は入力後にバリデーションで弾く。
 */
const toThresholdInput = (value: string): string => value.replace(/[^0-9]/g, '').slice(0, 3);

/** 保存時に親へ渡す内容（#3180） */
export interface MasterMappingFormResult {
  patternId: string;
  patternName: string;
  /** 検索キー。照合の主体。1件のみ。 */
  searchKey: MasterSearchKey;
  /** 絞り込みキー。AND 条件。0件可。 */
  filterKeys: MasterFilterKey[];
  /**
   * 自動反映の下限スコア（#3263）。0〜100の整数(%)。
   * 未入力なら undefined を渡し、バックエンドの既定値に委ねる。
   */
  autoApplyScore?: number;
  reviewThreshold?: number;
  /** AI照合リランク（#3355）。未設定は false 扱い。 */
  enableLlmRerank?: boolean;
  columnMappings: ColumnMapping[];
  // 紐づけ単位（取引先コードなど）の OCR 項目。任意。履歴をこの値ごとに分離する。
  scopeItemId: string;
  scopeItemLabel: string;
}

interface Props {
  open: boolean;
  /** 辞書照合の使用済み項目を取得するための会社ID（排他制御用） */
  companyId?: string | null;
  fileName: string;
  csvColumns: string[];
  patterns: PatternType[];
  initial?: MasterMappingType | null;
  /**
   * このマスタで既に確定している search_csv_column（マスタ側の照合キー）。
   * マスタ単位で 1 つの search_key しか持てない制約のため、
   * 値が渡された場合は select を disabled にして変更不可とする。
   */
  lockedSearchCsvColumn?: string;
  /**
   * パターン別設定タブから開いた場合の、固定するパターンID（#3180）。
   * 指定時はパターン選択を非表示にする（そのパターンのステップを追加する導線のため）。
   */
  fixedPatternId?: string;
  onClose: () => void;
  onSubmit: (data: MasterMappingFormResult) => Promise<void> | void;
}

/** 絞り込みキーの編集中の状態。ラベルは保存時に解決するため保持しない。 */
interface FilterKeyDraft {
  itemId: string;
  csvColumn: string;
  mode: MasterFilterKey['filter_match_mode'];
}

const MasterMappingFormModal = ({
  open,
  companyId,
  fileName,
  csvColumns,
  patterns,
  initial,
  lockedSearchCsvColumn,
  fixedPatternId,
  onClose,
  onSubmit,
}: Props): ReactElement => {
  const [selectedPatternId, setSelectedPatternId] = useState<string>('');
  // 検索キー（#3180）。1件のみ。
  const [searchItemId, setSearchItemId] = useState<string>('');
  const [searchCsvColumn, setSearchCsvColumn] = useState<string>('');
  const [matchMode, setMatchMode] = useState<MatchMode>(searchMatchMode.ai);
  // 絞り込みキー（#3180）。0件以上。AND 条件で候補を絞る。
  const [filterKeys, setFilterKeys] = useState<FilterKeyDraft[]>([]);
  // 自動反映の下限スコア（#3263）。未入力を表現するため文字列で持ち、保存時に数値へ変換する。
  const [autoApplyScore, setAutoApplyScore] = useState<string>('');
  const [reviewThreshold, setReviewThreshold] = useState<string>('');
  const [enableLlmRerank, setEnableLlmRerank] = useState<boolean>(
    masterMatchThreshold.defaultEnableLlmRerank,
  );
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  // 紐づけ単位（取引先コードなど）の OCR 項目。任意。
  const [scopeItemId, setScopeItemId] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  // 排他制御用: 選択中パターンで辞書照合に使われている search_item_id の集合
  const [dictionaryUsedItemIds, setDictionaryUsedItemIds] = useState<Set<string>>(new Set());
  // 排他制御用: 選択中パターンで「他のマスタ」が検索キーに使っている項目の集合（自マスタは除外）
  const [masterUsedItemIds, setMasterUsedItemIds] = useState<Set<string>>(new Set());
  // 注記用: 他マスタが取得項目の格納先にしている項目 → そのマスタ名（#3180）
  const [masterProducedItemIds, setMasterProducedItemIds] = useState<Map<string, string>>(
    new Map(),
  );

  useEffect(() => {
    if (!open) return;
    setSelectedPatternId(fixedPatternId ?? initial?.pattern_id ?? '');

    // 既存データ（search_key 未設定）は旧フィールドから読み替える（#3180）。
    const initialSearchKey = initial
      ? resolveSearchKey(initial)
      : { item_id: '', item_label: '', csv_column: '', match_mode: searchMatchMode.ai };
    setSearchItemId(initialSearchKey.item_id);
    setMatchMode(initialSearchKey.match_mode);
    // 照合先列の初期値:
    // - AI照合(ai): 照合先は embedding 生成列に固定されるため、ロック値を採用する。
    // - 部分一致(keyword): 任意の列を照合先にできるため、保存済みの列を優先する。
    //   （ロック値で上書きすると、編集で選び直した列が毎回 embedding 列に戻ってしまうため）
    setSearchCsvColumn(
      initialSearchKey.match_mode === searchMatchMode.keyword
        ? initialSearchKey.csv_column || lockedSearchCsvColumn || ''
        : lockedSearchCsvColumn || initialSearchKey.csv_column || '',
    );

    setFilterKeys(
      initial
        ? resolveFilterKeys(initial).map(key => ({
            itemId: key.item_id,
            csvColumn: key.csv_column,
            mode: key.filter_match_mode,
          }))
        : [],
    );
    // 未設定なら既定値を初期表示する（#3263）。
    // 数値 0 を「未設定」と混同しないよう null 判定にする（0% は「常に自動反映」の意味を持つ）。
    const initialAutoApply = initial?.auto_apply_score;
    setAutoApplyScore(
      initialAutoApply != null
        ? String(initialAutoApply)
        : String(masterMatchThreshold.defaultAutoApplyScore),
    );
    // 要注意にする下限スコア（#3309）。未設定なら既定値を初期表示する
    const initialReviewThreshold = initial?.review_threshold;
    setReviewThreshold(
      initialReviewThreshold != null
        ? String(initialReviewThreshold)
        : String(masterMatchThreshold.defaultReviewThreshold),
    );
    // AI照合リランク（#3355）。未設定は OFF。
    setEnableLlmRerank(initial?.enable_llm_rerank ?? masterMatchThreshold.defaultEnableLlmRerank);
    setColumnMappings(initial?.column_mappings ?? []);
    setScopeItemId(initial?.scope_item_id ?? '');
  }, [open, initial, lockedSearchCsvColumn, fixedPatternId]);

  // 指定パターンで辞書照合に使用済みの search_item_id 集合を取得する（失敗時は空Setにフォールバック）
  const fetchDictionaryUsedItemIds = useCallback(
    async (patternId: string): Promise<Set<string>> => {
      if (!companyId || !patternId) return new Set();
      try {
        const dictionaries = await getDictionariesByPatternId(companyId, patternId);
        return new Set(dictionaries.map(d => d.search_item_id).filter((id): id is string => !!id));
      } catch {
        return new Set();
      }
    },
    [companyId],
  );

  // 指定パターンで「他のマスタ」が使っている項目を取得する（自分自身は除外）。
  //   usedItemIds     : 他マスタが検索キーにしている項目。同じ項目を複数マスタの検索キーに
  //                     すると照合が競合するため、検索キーの選択肢から除外する。
  //                     ※ 絞り込みキーは値を条件として読むだけで競合しないため制限しない（#3180）。
  //   producedItemIds : 他マスタが取得項目の格納先にしている項目 → そのマスタ名。
  //                     「前のステップの照合結果を絞り込みに使う」意図的な設定であることを
  //                     選択肢の注記で示すために使う（#3180）。
  const fetchMasterItemUsage = useCallback(
    async (
      patternId: string,
    ): Promise<{ usedItemIds: Set<string>; producedItemIds: Map<string, string> }> => {
      const empty = { usedItemIds: new Set<string>(), producedItemIds: new Map<string, string>() };
      if (!companyId || !patternId) return empty;
      try {
        const masters = await getMastersByPatternId(companyId, patternId);
        const others = masters.filter(m => m.master_id !== initial?.master_id);

        const usedItemIds = new Set(
          others.map(m => m.search_item_id).filter((id): id is string => !!id),
        );
        const producedItemIds = new Map<string, string>();
        for (const master of others) {
          for (const columnMapping of master.column_mappings ?? []) {
            if (!columnMapping.item_id) continue;
            // 同じ項目を複数マスタが出力することは通常ないため、最初に見つけたマスタ名を採用する。
            if (!producedItemIds.has(columnMapping.item_id)) {
              producedItemIds.set(columnMapping.item_id, master.file_name);
            }
          }
        }
        return { usedItemIds, producedItemIds };
      } catch {
        return empty;
      }
    },
    [companyId, initial?.master_id],
  );

  // モーダル表示中にパターンが変わったら使用済み項目（辞書・他マスタ）を再取得（閉じている間は取得しない）
  useEffect(() => {
    if (!open) return;
    let active = true;
    void fetchDictionaryUsedItemIds(selectedPatternId).then(ids => {
      if (active) setDictionaryUsedItemIds(ids);
    });
    void fetchMasterItemUsage(selectedPatternId).then(usage => {
      if (!active) return;
      setMasterUsedItemIds(usage.usedItemIds);
      setMasterProducedItemIds(usage.producedItemIds);
    });
    return () => {
      active = false;
    };
  }, [open, fetchDictionaryUsedItemIds, fetchMasterItemUsage, selectedPatternId]);

  const selectedPattern = patterns.find(p => p.patternId === selectedPatternId);
  const patternItems: PatternItem[] = useMemo(() => {
    if (!selectedPattern?.item_list) return [];
    return selectedPattern.item_list.flatMap((item: ItemListType) => {
      const isArray = item.type === 'array';
      const parentItem = { id: item.id, label: item.parentValue, isArray };
      const childItems = item.children.map(child => ({
        id: child.id,
        label: `${item.parentValue} > ${child.childValue}`,
        isArray,
      }));
      return childItems.length > 0 ? childItems : [parentItem];
    });
  }, [selectedPattern]);

  const resolveItemLabel = useCallback(
    (itemId: string): string => patternItems.find(i => i.id === itemId)?.label ?? '',
    [patternItems],
  );

  // 照合履歴と一緒に記録する項目は、既存マッピングが既に設定済みの場合は変更不可（#2905）。
  // 編集時に initial.scope_item_id が入っているときのみロックする（新規・未設定編集はロックしない）。
  const isScopeLocked = !!initial?.scope_item_id;

  // ---- バリデーション（#3180）----
  // 絞り込みキーは AND 条件のため、同じ項目・同じ列を重ねても意味がない。
  // 検索キーと同じ項目を絞り込みに使うのも、照合対象を自分自身で絞ることになり成立しない。
  const duplicatedFilterItemIds = useMemo(() => {
    const seen = new Set<string>();
    const duplicated = new Set<string>();
    for (const key of filterKeys) {
      if (!key.itemId) continue;
      if (seen.has(key.itemId)) duplicated.add(key.itemId);
      seen.add(key.itemId);
    }
    return duplicated;
  }, [filterKeys]);

  const duplicatedFilterCsvColumns = useMemo(() => {
    const seen = new Set<string>();
    const duplicated = new Set<string>();
    for (const key of filterKeys) {
      if (!key.csvColumn) continue;
      if (seen.has(key.csvColumn)) duplicated.add(key.csvColumn);
      seen.add(key.csvColumn);
    }
    return duplicated;
  }, [filterKeys]);

  // 複数行データ（明細）の子項目は絞り込みに使えない（#3517）。
  // 照合は「同じ明細の同じ行」か単一項目からしか絞り込みの値を拾えず、別の明細の項目は
  // 自動照合では常に未適用になる。再照合では同じ番号の行の値を拾ってしまい、
  // 自動照合と結果がずれる。設定済みのものは単一項目に変えてもらう前提で、選べないようにする。
  const arrayItemIds = useMemo(
    () => new Set(patternItems.filter(item => item.isArray).map(item => item.id)),
    [patternItems],
  );

  const filterKeyErrorAt = (index: number): string => {
    const key = filterKeys[index];
    if (key.itemId && arrayItemIds.has(key.itemId)) {
      return '※ 複数行データの項目は絞り込みに使えません。単一項目に変更してください';
    }
    if (!key.itemId || !key.csvColumn) return '';
    if (key.itemId === searchItemId) return '※ 照合キーと同じ項目は絞り込みに使えません';
    if (duplicatedFilterItemIds.has(key.itemId)) return '※ 同じ項目は複数の条件に使えません';
    if (duplicatedFilterCsvColumns.has(key.csvColumn)) return '※ 同じ列は複数の条件に使えません';
    return '';
  };

  const hasFilterKeyError = filterKeys.some(
    key =>
      !key.itemId ||
      !key.csvColumn ||
      arrayItemIds.has(key.itemId) ||
      key.itemId === searchItemId ||
      duplicatedFilterItemIds.has(key.itemId) ||
      duplicatedFilterCsvColumns.has(key.csvColumn),
  );

  /**
   * 自動反映の下限スコア入力（%）を検証する（#3263）。
   * 空欄は「既定値を使う」を意味するためエラーにしない。
   */
  const thresholdErrorOf = (value: string): string => {
    if (value === '') return '';
    const parsed = Number(value);
    if (!Number.isInteger(parsed)) return '※ 整数で入力してください';
    if (parsed < masterMatchThreshold.min || parsed > masterMatchThreshold.max) {
      return `※ ${masterMatchThreshold.min}〜${masterMatchThreshold.max} の範囲で入力してください`;
    }
    return '';
  };

  const autoApplyScoreError = thresholdErrorOf(autoApplyScore);
  const reviewThresholdError = thresholdErrorOf(reviewThreshold);

  const hasThresholdError = !!autoApplyScoreError || !!reviewThresholdError;

  // STEP番号（#3180）。パターン別設定タブから開いた場合はパターン選択が無いため、
  // その分ずれる。ハードコードすると番号が飛ぶので、表示するセクションから算出する。
  const stepNumbers = useMemo(() => {
    let current = fixedPatternId ? 0 : 1; // パターン選択があれば STEP1 を消費する
    return {
      filter: ++current,
      searchKey: ++current,
      threshold: ++current,
      columnMappings: ++current,
      scope: ++current,
      reviewThreshold: ++current,
    };
  }, [fixedPatternId]);

  const canSubmit =
    !!selectedPatternId &&
    !!searchItemId &&
    !!searchCsvColumn &&
    !hasFilterKeyError &&
    !hasThresholdError &&
    columnMappings.every(m => m.csv_column && m.item_id);

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    const patternName = selectedPattern?.pattern_name ?? '';
    setSubmitting(true);
    try {
      await onSubmit({
        patternId: selectedPatternId,
        patternName,
        searchKey: {
          item_id: searchItemId,
          item_label: resolveItemLabel(searchItemId),
          csv_column: searchCsvColumn,
          match_mode: matchMode,
        },
        // 空欄なら undefined を渡し、バックエンドの既定値にフォールバックさせる（#3263）。
        autoApplyScore: autoApplyScore !== '' ? Number(autoApplyScore) : undefined,
        reviewThreshold: reviewThreshold !== '' ? Number(reviewThreshold) : undefined,
        // 部分一致では並び替えの余地がないため、AI照合のときだけ保存する（#3355）
        enableLlmRerank: matchMode === searchMatchMode.ai ? enableLlmRerank : false,
        filterKeys: filterKeys.map(key => ({
          item_id: key.itemId,
          item_label: resolveItemLabel(key.itemId),
          csv_column: key.csvColumn,
          filter_match_mode: key.mode,
        })),
        columnMappings,
        scopeItemId,
        scopeItemLabel: scopeItemId ? resolveItemLabel(scopeItemId) : '',
      });
    } finally {
      setSubmitting(false);
    }
  };

  /** 項目セレクトの選択肢。検索キーでは他マスタ・辞書の使用済み項目を非活性にする。 */
  const buildItemOptions = (
    isSearchKey: boolean,
  ): { option: string; value: string; disabled?: boolean }[] =>
    patternItems.flatMap(item => {
      const usedByMaster = masterUsedItemIds.has(item.id);
      const usedByDictionary = dictionaryUsedItemIds.has(item.id);
      // 他マスタの取得項目は「前のステップで値が入る項目」。
      // 意図的に絞り込み・照合へ使えるよう、非活性にはせず由来だけ注記する（#3180）。
      const producedBy = masterProducedItemIds.get(item.id);
      const producedSuffix = producedBy ? `（${producedBy}の取得項目）` : '';

      // 絞り込みキーは値を条件として読むだけなので、他マスタ・辞書とは競合しない。
      if (!isSearchKey) {
        // 複数行データの子項目は絞り込みに使えない（#3517）。
        // 選択肢から消すと「なぜ出てこないのか」が分からないため、理由を添えて非活性で出す。
        // 設定済みのものも同じ表示になり、保存はエラーで止める。
        if (item.isArray) {
          return [
            {
              option: `${item.label}（複数行データのため選択不可）`,
              value: item.id,
              disabled: true,
            },
          ];
        }
        return [{ option: `${item.label}${producedSuffix}`, value: item.id }];
      }

      const suffix = usedByMaster
        ? '（他のマスタ設定済み）'
        : usedByDictionary
          ? '（辞書変換設定済み）'
          : producedSuffix;
      return [
        {
          option: `${item.label}${suffix}`,
          value: item.id,
          disabled: usedByMaster || usedByDictionary,
        },
      ];
    });

  return (
    <Dialog open={open} sx={styles.dialog}>
      <CustomLoading loading={submitting} />
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>
          {initial ? 'マスタ照合の編集' : 'マスタ照合の追加'}
        </Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.closeIcon} />
        </IconButton>
      </Box>

      <Box sx={styles.fileSummary}>
        <Typography sx={regularSm}>対象マスタ：{fileName}</Typography>
      </Box>

      {/* パターン別設定タブから開いた場合はパターンが決まっているため選択させない（#3180） */}
      {!fixedPatternId && (
        <>
          <StepHeader step={1} title="読取パターンを選ぶ" />
          <CustomSelectField
            labelText=""
            list={patterns.map(p => ({ option: p.pattern_name, value: p.patternId }))}
            value={selectedPatternId}
            placeholder="選択してください"
            onChange={(e): void => {
              setSelectedPatternId(String(e.target.value));
              setSearchItemId('');
              setFilterKeys([]);
              setColumnMappings([]);
              setScopeItemId('');
            }}
            formMb="16px"
            height="40px"
            width="100%"
          />
        </>
      )}

      {/* ---- 絞り込み条件（#3180）----
          実際の処理順（絞ってから検索する）と画面の並びを揃えるため、照合キーより上に置く。 */}
      {selectedPatternId && patternItems.length > 0 && csvColumns.length > 0 && (
        <Box sx={styles.sectionDivider}>
          <StepHeader step={stepNumbers.filter} title="絞り込み条件" optional />
          <Typography sx={styles.sectionDescription}>
            すべての条件に一致する行だけを照合の対象にします。
            <br />
            前のマスタ照合で取得した値（得意先IDなど）も指定できます。
            <br />
            複数行データの項目は使えません。
          </Typography>

          {filterKeys.map((filterKey, index) => {
            const errorText = filterKeyErrorAt(index);
            return (
              <Box key={index} sx={{ marginBottom: '8px' }}>
                <Box sx={styles.keyRow}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <CustomSelectField
                      labelText=""
                      list={buildItemOptions(false)}
                      value={filterKey.itemId}
                      placeholder="項目（パターン項目）"
                      onChange={(e): void => {
                        const updated = [...filterKeys];
                        updated[index] = { ...updated[index], itemId: String(e.target.value) };
                        setFilterKeys(updated);
                      }}
                      formMb="0px"
                      height="40px"
                      width="100%"
                    />
                  </Box>
                  <Typography sx={{ ...regularSm, color: gray80 }}>→</Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <CustomSelectField
                      labelText=""
                      list={csvColumns.map(col => ({ option: col, value: col }))}
                      value={filterKey.csvColumn}
                      placeholder="マスタ列"
                      onChange={(e): void => {
                        const updated = [...filterKeys];
                        updated[index] = { ...updated[index], csvColumn: String(e.target.value) };
                        setFilterKeys(updated);
                      }}
                      formMb="0px"
                      height="40px"
                      width="100%"
                    />
                  </Box>
                  <Box sx={{ width: '110px', flexShrink: 0 }}>
                    <CustomSelectField
                      labelText=""
                      list={[
                        { option: '完全一致', value: filterMatchMode.exact },
                        { option: '部分一致', value: filterMatchMode.keyword },
                      ]}
                      value={filterKey.mode}
                      onChange={(e): void => {
                        const updated = [...filterKeys];
                        updated[index] = {
                          ...updated[index],
                          mode:
                            String(e.target.value) === filterMatchMode.keyword
                              ? filterMatchMode.keyword
                              : filterMatchMode.exact,
                        };
                        setFilterKeys(updated);
                      }}
                      formMb="0px"
                      height="40px"
                      width="100%"
                    />
                  </Box>
                  <IconButton
                    onClick={(): void => {
                      setFilterKeys(filterKeys.filter((_, i) => i !== index));
                    }}
                    sx={{ padding: '4px' }}
                  >
                    <RemoveCircleOutlineIcon sx={{ color: orange50, fontSize: '20px' }} />
                  </IconButton>
                </Box>
                {errorText && <Typography sx={styles.noteText}>{errorText}</Typography>}
              </Box>
            );
          })}

          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={(): void => {
              setFilterKeys([
                ...filterKeys,
                // 絞り込み用途の値はIDコードであることが多く、部分一致より誤照合しにくいため完全一致を既定にする。
                { itemId: '', csvColumn: '', mode: filterMatchMode.exact },
              ]);
            }}
            sx={styles.addButton}
          >
            絞り込み条件を追加
          </Button>
        </Box>
      )}

      {/* ---- 照合キー（#3180）---- */}
      {selectedPatternId && patternItems.length > 0 && (
        <Box sx={styles.sectionDivider}>
          <StepHeader step={stepNumbers.searchKey} title="照合キー" />
          <Typography sx={styles.sectionDescription}>
            絞り込んだ行の中から、この項目で照合します。
          </Typography>

          <Typography sx={{ ...regularSm, color: gray80, marginBottom: '4px' }}>
            照合方式
          </Typography>
          <Typography sx={styles.sectionDescription}>
            AI照合：意味の近さで照合します（商品名など）。
            <br />
            部分一致：値がそのまま含まれるかで照合します（商品コード・JANコードなど）。
          </Typography>
          <CustomSelectField
            labelText=""
            list={[
              { option: 'AI照合', value: searchMatchMode.ai },
              { option: '部分一致', value: searchMatchMode.keyword },
            ]}
            value={matchMode}
            onChange={(e): void => {
              const next =
                String(e.target.value) === searchMatchMode.keyword
                  ? searchMatchMode.keyword
                  : searchMatchMode.ai;
              setMatchMode(next);
              // AI照合に戻す場合、照合先列を embedding 済みの列（登録時の search_key）に戻す（#3027）。
              // 部分一致で embedding されていない列を選んだまま AI照合に切り替わると、
              // embedding が無い列で照合できるように見える不正な設定になるのを防ぐ。
              if (next === searchMatchMode.ai && lockedSearchCsvColumn) {
                setSearchCsvColumn(lockedSearchCsvColumn);
              }
            }}
            formMb="16px"
            height="40px"
            width="100%"
          />

          <Typography sx={styles.sectionLabel}>照合元項目（パターン項目）</Typography>
          <Typography sx={styles.sectionDescription}>OCR結果のどの値で検索するか</Typography>
          <CustomSelectField
            labelText=""
            list={buildItemOptions(true)}
            value={searchItemId}
            placeholder="選択してください"
            onChange={(e): void => setSearchItemId(String(e.target.value))}
            formMb="16px"
            height="40px"
            width="100%"
          />

          {csvColumns.length > 0 && (
            <>
              <Typography sx={styles.sectionLabel}>照合先項目（マスタ項目）</Typography>
              <Typography sx={styles.sectionDescription}>
                マスタCSVのどのカラムで検索するか
              </Typography>
              <CustomSelectField
                labelText=""
                list={csvColumns.map(col => ({ option: col, value: col }))}
                value={searchCsvColumn}
                placeholder="選択してください"
                onChange={(e): void => setSearchCsvColumn(String(e.target.value))}
                formMb="4px"
                height="40px"
                width="100%"
                // 部分一致は embedding を使わず raw_data の任意列に ILIKE するため、照合先列を選び直せる（#3022）。
                // AI照合は embedding が特定列で生成済みのため、登録済みマスタでは変更不可（従来通り）。
                disabled={matchMode === searchMatchMode.ai && !!lockedSearchCsvColumn}
              />
              <Typography sx={styles.noteText}>
                {matchMode === searchMatchMode.keyword
                  ? '※ 部分一致では、照合先の項目（マスタのカラム）を自由に選べます。'
                  : lockedSearchCsvColumn
                    ? '※ このマスタはこの項目を基準に登録済みのため、変更できません。別の項目で照合したい場合は、新しいマスタとして登録してください。'
                    : '※ この項目を基準にマスタ照合を行います。一度アップロードすると変更できません。'}
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* ---- 自動反映の下限スコア（#3263）---- */}
      {selectedPatternId && patternItems.length > 0 && (
        <Box sx={styles.sectionDivider}>
          <StepHeader step={stepNumbers.threshold} title="自動で反映する条件" optional />
          <Typography sx={styles.sectionDescription}>
            OCR直後の自動照合で、マスタの値をどのくらい似ていたら反映するかを決めます。
            <br />
            一致度がこの数値に届かない場合は自動で反映せず、あとから手動で照合できます。
            <br />※ 手動の再照合には影響しません（候補はスコアに関わらずすべて表示されます）。
          </Typography>

          <CustomTextfield
            labelText=""
            value={autoApplyScore}
            placeholder={String(masterMatchThreshold.defaultAutoApplyScore)}
            onChange={(e): void => setAutoApplyScore(toThresholdInput(e.target.value))}
            error={!!autoApplyScoreError}
            endAdornment={<Typography sx={styles.unitText}>% 以上で反映</Typography>}
            formMb="0px"
            height="40px"
            width="100%"
            inputWidth="100%"
          />

          {autoApplyScoreError ? (
            <Typography sx={{ ...styles.noteText, marginTop: '4px' }}>
              {autoApplyScoreError}
            </Typography>
          ) : (
            <Typography sx={{ ...styles.sectionDescription, marginTop: '4px' }}>
              数値を上げるほど慎重になり（誤った自動反映が減る代わりに手動照合が増える）、
              下げるほど自動で反映されやすくなります。
            </Typography>
          )}

          {/* ---- AI照合リランク（#3355）。部分一致では並び替えの余地がないため出さない ---- */}
          {matchMode === searchMatchMode.ai && (
            <Box sx={{ marginTop: '16px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableLlmRerank}
                    onChange={(e): void => setEnableLlmRerank(e.target.checked)}
                  />
                }
                label={<Typography sx={{ fontSize: '14px' }}>AIで候補を選び直す</Typography>}
              />
              <Typography sx={{ ...styles.sectionDescription, marginTop: '4px' }}>
                似た名前が多く、上位の候補を取り違えやすいマスタで有効にします。
                <br />
                候補の並び順だけを見直します（一致度の数値は変わりません）。
                <br />※ 一致度が高く他の候補を引き離している場合は、そのまま使うためAIは動きません。
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {csvColumns.length > 0 && selectedPatternId && (
        <Box sx={styles.sectionDivider}>
          <StepHeader step={stepNumbers.columnMappings} title="取得項目 → 格納先項目" />
          <Typography sx={styles.sectionDescription}>
            照合に成功した行から取り出すマスタ項目と、OCR結果のどの項目に格納するかを設定します
          </Typography>
          {columnMappings.map((mapping, index) => (
            <Box key={index} sx={styles.keyRow}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CustomSelectField
                  labelText=""
                  list={csvColumns.map(col => ({ option: col, value: col }))}
                  value={mapping.csv_column}
                  placeholder="取得項目（マスタ項目）"
                  onChange={(e): void => {
                    const updated = [...columnMappings];
                    updated[index] = { ...updated[index], csv_column: String(e.target.value) };
                    setColumnMappings(updated);
                  }}
                  formMb="0px"
                  height="40px"
                  width="100%"
                />
              </Box>
              <Typography sx={{ ...regularSm, color: gray80 }}>→</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CustomSelectField
                  labelText=""
                  // 照合キー自身も選べる（#3339）。指定すると照合結果の値で
                  // 照合キー欄を上書きする。未指定なら OCR 読取値のまま（#2915）。
                  // 元の読取値は ocr_value に退避されるので「OCR結果：」表示は残る。
                  list={patternItems.map(i => ({ option: i.label, value: i.id }))}
                  value={mapping.item_id}
                  placeholder="格納先項目（パターン項目）"
                  onChange={(e): void => {
                    const updated = [...columnMappings];
                    const selectedItem = patternItems.find(i => i.id === e.target.value);
                    updated[index] = {
                      ...updated[index],
                      item_id: String(e.target.value),
                      item_label: selectedItem?.label || '',
                    };
                    setColumnMappings(updated);
                  }}
                  formMb="0px"
                  height="40px"
                  width="100%"
                />
              </Box>
              <IconButton
                onClick={(): void => {
                  setColumnMappings(columnMappings.filter((_, i) => i !== index));
                }}
                sx={{ padding: '4px' }}
              >
                <RemoveCircleOutlineIcon sx={{ color: orange50, fontSize: '20px' }} />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={(): void => {
              setColumnMappings([
                ...columnMappings,
                { csv_column: '', item_id: '', item_label: '' },
              ]);
            }}
            sx={styles.addButton}
          >
            取得項目を追加
          </Button>
        </Box>
      )}

      {selectedPatternId && patternItems.length > 0 && (
        <Box sx={styles.sectionDivider}>
          <StepHeader step={stepNumbers.scope} title="照合履歴と一緒に記録する項目" optional />
          <Typography sx={styles.sectionDescription}>
            ここで指定した項目（例：取引先コード）の値を照合履歴と一緒に記録し、次回以降の照合時に優先的に参照します。
            明細（複数行）の項目を指定した場合は、1件目の行の値が記録されます。
          </Typography>
          <CustomSelectField
            labelText=""
            list={[
              { option: '（指定しない）', value: '' },
              ...patternItems
                .filter(i => i.id !== searchItemId)
                // 明細（配列）項目は行が複数ありうるが、記録されるのは1件目の値のみ。
                // 保存する scope_item_label は素のラベルのままにし、表示だけ補足する（#3072）。
                .map(i => ({
                  option: i.isArray ? `${i.label}（1件目の値）` : i.label,
                  value: i.id,
                })),
            ]}
            value={scopeItemId}
            placeholder="選択してください"
            onChange={(e): void => {
              // CustomSelectField の placeholder MenuItem は value="__placeholder__"。
              // 「選択してください」に戻したときに __placeholder__ が保存値へ混入するのを防ぎ、
              // 未指定（空文字）に正規化する（#2905）。
              const next = String(e.target.value);
              setScopeItemId(next === '__placeholder__' ? '' : next);
            }}
            formMb="0px"
            height="40px"
            width="100%"
            // 照合履歴と一緒に記録する項目は、保存後は変更不可（#2905）。
            // 既存履歴の supplier_id 整合性を壊さないため、編集時にロックする。
            disabled={isScopeLocked}
          />
          <Typography sx={{ ...styles.noteText, marginTop: '4px' }}>
            ※ この項目は照合履歴の記録単位のため、一度設定すると変更できません。
          </Typography>
        </Box>
      )}

      {/* ---- 要注意にする条件（#3309）。自動反映とは別の軸なので STEP を分ける ---- */}
      {selectedPatternId && patternItems.length > 0 && (
        <Box sx={styles.sectionDivider}>
          <StepHeader step={stepNumbers.reviewThreshold} title="要注意にする条件" optional />
          <Typography sx={styles.sectionDescription}>
            一致度がこの数値に届かない照合結果に「要注意」を付けます。
            <br />
            反映はされるが人にも確認してほしい、という範囲をここで決めます。
          </Typography>

          <CustomTextfield
            labelText=""
            value={reviewThreshold}
            placeholder={String(masterMatchThreshold.defaultReviewThreshold)}
            onChange={(e): void => setReviewThreshold(toThresholdInput(e.target.value))}
            error={!!reviewThresholdError}
            endAdornment={<Typography sx={styles.unitText}>% 未満で要注意</Typography>}
            formMb="0px"
            height="40px"
            width="100%"
            inputWidth="100%"
          />

          {reviewThresholdError ? (
            <Typography sx={{ ...styles.noteText, marginTop: '4px' }}>
              {reviewThresholdError}
            </Typography>
          ) : (
            <Typography sx={{ ...styles.sectionDescription, marginTop: '4px' }}>
              自動で反映する条件より高くすると、「反映はするが要注意も付ける」範囲ができます。
            </Typography>
          )}
        </Box>
      )}

      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onClose} disabled={submitting}>
          <Typography sx={styles.cancelButtonText}>キャンセル</Typography>
        </Button>
        <Button
          sx={styles.submitButton}
          onClick={(): void => {
            void handleSubmit();
          }}
          disabled={!canSubmit || submitting}
        >
          <Typography sx={styles.submitButtonText}>保存</Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default MasterMappingFormModal;
